import { Alert } from "@mui/material";
import * as React from "react";

import {
  useUpdateWorkspaceAccessibility,
  useWorkspaceAccessibility,
  WorkspaceAccessibilityLevel,
} from "@/shared/domain/workspaces";
import { RobotoUnauthorizedException } from "@/shared/http";

import { AccessibilityConfirmationView } from "./AccessibilityConfirmationView";
import { AccessibilityDropdown } from "./AccessibilityDropdown";

interface AccessibilityContentProps {
  workspaceId: string;
}

type ModalView =
  | { type: "main" }
  | { type: "confirmation"; targetAccessibility: WorkspaceAccessibilityLevel };

export const AccessibilityContent: React.FC<AccessibilityContentProps> = ({
  workspaceId,
}) => {
  const [modalView, setModalView] = React.useState<ModalView>({ type: "main" });
  const [updateError, setUpdateError] = React.useState<string | null>(null);

  const {
    data: accessibilityData,
    isLoading,
    isError,
    refetch,
  } = useWorkspaceAccessibility(workspaceId);

  const updateAccessibility = useUpdateWorkspaceAccessibility();

  const handleAccessibilityChange = React.useCallback(
    (newAccessibility: WorkspaceAccessibilityLevel) => {
      setUpdateError(null);
      setModalView({
        type: "confirmation",
        targetAccessibility: newAccessibility,
      });
    },
    [],
  );

  const handleConfirm = React.useCallback(() => {
    if (modalView.type !== "confirmation") {
      return;
    }

    updateAccessibility.mutate(
      {
        workspaceId,
        accessibility: modalView.targetAccessibility,
      },
      {
        onSuccess: () => {
          setModalView({ type: "main" });
          setUpdateError(null);
        },
        onError: (err) => {
          setModalView({ type: "main" });
          const message =
            err instanceof RobotoUnauthorizedException
              ? "You don't have permission to change this workspace's accessibility settings."
              : err.message || "Failed to update accessibility";
          setUpdateError(message);
        },
      },
    );
  }, [modalView, workspaceId, updateAccessibility]);

  const handleCancel = React.useCallback(() => {
    setModalView({ type: "main" });
  }, []);

  if (modalView.type === "confirmation" && accessibilityData) {
    return (
      <AccessibilityConfirmationView
        workspaceId={workspaceId}
        targetAccessibility={modalView.targetAccessibility}
        datasets={accessibilityData.datasets}
        currentWorkspaceAccessibility={
          accessibilityData.workspace_accessibility
        }
        isUpdating={updateAccessibility.isPending}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <>
      {updateError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setUpdateError(null)}
        >
          {updateError}
        </Alert>
      )}
      <AccessibilityDropdown
        currentStatus={accessibilityData?.overall_status}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        onAccessibilityChange={handleAccessibilityChange}
      />
    </>
  );
};
